import {useCallback, useEffect, useRef, useState} from "react";
import {nextSpinRotation} from "@/components/games/spinRotation";
import {makeApiError} from "@/redux/meta/metaActions";

/**
 * One spin, from click to result.
 *
 * `SurfaceWheel` was meant to be `DailyWheelModal` reskinned, and it copied the
 * shape without the part that mattered: it added the server's absolute
 * `rotationDegrees` to the previous angle instead of turning forward to the
 * winning slice, so its second spin landed on the wrong prize. That is the cost
 * of two components owning the same behaviour — the fix lands in one of them
 * and the other keeps the bug until a member reports it.
 *
 * What varies between wheels is the endpoint, the animation length and the
 * copy. None of that is the SEQUENCE, which is identical everywhere: disable
 * the control, ask the server, turn the wheel to the slice it named, then
 * reveal once the wheel has actually stopped. That sequence lives here.
 *
 * Used by `DailyWheelModal`, `SurfaceWheel` and `WheelSpinRenderer`.
 * `MinelabRttrWheel`, `FuelUpSpinWheelModal` and `ProgrammaticSpinWheel` still
 * own their own — RTTR is live and draws to a different convention, so it is
 * deliberately left alone rather than migrated under a shipped campaign.
 *
 * The reveal is held behind the animation on purpose — a result that appears
 * while the wheel is still turning tells the member the spin was theatre.
 */
export interface SpinSessionResult {
    outcomeIndex: number;
}

export interface UseSpinSessionOptions<T extends SpinSessionResult> {
    /** Calls the spin endpoint. The server picks the outcome, never the client. */
    spin: () => Promise<T>;
    /** How many slices the wheel is drawing — the landing angle needs it. */
    segmentCount: number;
    /** Must match the CSS transition, so the result lands with the animation. */
    animationMs: number;
    /** Whole turns of flourish. Whole, so the landing angle stays exact. */
    fullTurns?: number;
}

export interface SpinSession<T> {
    /** Current wheel angle in degrees. Feed straight to the renderer. */
    rotation: number;
    /** True from the click until the wheel has stopped. */
    spinning: boolean;
    /** The server's result, published only once the wheel has stopped. */
    result: T | null;
    /** Whatever the spin endpoint refused with, for the caller to render. */
    error: string | null;
    /** Start a spin. Ignored while one is in flight. */
    onSpin: () => Promise<void>;
    /** Clear the last result — for a caller that reopens the same wheel. */
    reset: () => void;
}

export function useSpinSession<T extends SpinSessionResult>({
    spin, segmentCount, animationMs, fullTurns,
}: UseSpinSessionOptions<T>): SpinSession<T> {
    const [rotation, setRotation] = useState(0);
    const [spinning, setSpinning] = useState(false);
    const [result, setResult] = useState<T | null>(null);
    const [error, setError] = useState<string | null>(null);

    /*
     * Held so unmounting mid-spin cancels the reveal. Without it the timer
     * writes state into a component that is gone — and a viewer who reopens
     * sees a result they never watched land.
     */
    const revealTimer = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(revealTimer.current), []);

    /*
     * REDUCED MOTION SHORTENS THE WAIT, NOT JUST THE ANIMATION.
     *
     * `SpinWheel` already drops its transition to 0ms under the OS setting, so
     * the wheel arrives on the winning slice instantly — but the reveal was
     * still held for the full animation length, leaving a member who asked for
     * less motion staring at a finished wheel with no result for four seconds.
     * Read at spin time rather than at mount so a setting changed mid-session
     * takes effect on the next spin.
     */
    const revealDelay = useCallback((ms: number): number => {
        const reduced = typeof window !== "undefined" && typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        return reduced ? 0 : ms;
    }, []);

    const reset = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    const onSpin = useCallback(async () => {
        if (spinning) return;
        setSpinning(true);
        setResult(null);
        setError(null);
        try {
            const spun = await spin();
            setRotation(prev => nextSpinRotation(prev, spun.outcomeIndex, segmentCount, fullTurns));
            revealTimer.current = window.setTimeout(() => {
                setResult(spun);
                setSpinning(false);
            }, revealDelay(animationMs));
        } catch (err) {
            /*
             * `makeApiError`, NOT `err.message`. The generated client throws a
             * `ResponseError extends Error` whose message is the literal string
             * "Response returned an error code" — so reading `.message` printed
             * that under the wheel while the real reason ("You have no spins
             * remaining") sat unparsed on `err.response`.
             *
             * The wheel does not turn on a failure either: stopping here leaves
             * it where it was rather than spinning to an outcome that never
             * happened.
             */
            const parsed = await makeApiError(err);
            setError(parsed?.message || "The wheel could not be spun.");
            setSpinning(false);
        }
    }, [spinning, spin, segmentCount, animationMs, fullTurns, revealDelay]);

    return {rotation, spinning, result, error, onSpin, reset};
}
